import { debounce } from 'lodash';

import {
  Api,
  getCategoryList,
  recountTotal,
  recountProductsOverlays,
  getCategoryPos,
  getColumnSize,
  showOnboardingMessage,
} from './common';
import { clone } from '../../../../common/common';
import { PRODUCTS_TAB, switchTab } from './tabs';

// ------------------------------------
// Constants
// ------------------------------------
export const MOOD_CATEGORY_LOADING = 'MOOD_CATEGORY_LOADING';
export const MOOD_REMOVE_PRODUCT = 'MOOD_REMOVE_PRODUCT';
export const MOOD_UPDATE_PRODUCT = 'MOOD_UPDATE_PRODUCT';
export const MOOD_SELECT_PRODUCT = 'MOOD_SELECT_PRODUCT';
export const MOOD_SEARCH_PRODUCT_BY_IMAGE = 'MOOD_SEARCH_PRODUCT_BY_IMAGE';
export const MOOD_CATEGORY_SWITCH_RENAME = 'MOOD_CATEGORY_SWITCH_RENAME';
export const MOOD_CATEGORY_RENAME = 'MOOD_CATEGORY_RENAME';
export const MOOD_CATEGORY_CREATE = 'MOOD_CATEGORY_CREATE';
export const MOOD_CATEGORY_REMOVE = 'MOOD_CATEGORY_REMOVE';
export const MOOD_CATEGORY_CATEGORY_RELEASE = 'MOOD_CATEGORY_CATEGORY_RELEASE';
export const MOOD_CATEGORY_EXCLUDE_FROM_TOTAL = 'MOOD_CATEGORY_EXCLUDE_FROM_TOTAL';
export const MOOD_CATEGORY_PRIMARY_PIECE = 'MOOD_CATEGORY_PRIMARY_PIECE';
export const MOOD_RECOUNT_TOTAL = 'MOOD_RECOUNT_TOTAL';
export const MOOD_CATEGORY_TOGGLE_FAVOURITE = 'MOOD_CATEGORY_TOGGLE_FAVOURITE';
export const MOOD_CATEGORY_COPY_DIALOG = 'MOOD_CATEGORY_COPY_DIALOG';
export const MOOD_CATEGORY_CATEGORY_RELEASE_REMOVE = 'MOOD_CATEGORY_CATEGORY_RELEASE_REMOVE';
export const MOOD_CATEGORY_PLACEHOLDER = 'MOOD_CATEGORY_PLACEHOLDER';
export const MOOD_DROP_BEFORE_CREATE = 'MOOD_DROP_BEFORE_CREATE';

export const CREATE_PLACEHOLDER_ACTION = 'create';
export const REMOVE_PLACEHOLDER_ACTION = 'remove';

// ------------------------------------
// Actions
// ------------------------------------

export function toggleCategoryRelease(category) {
  return (dispatch, getState) => {
    return new Promise(resolve => {
      const categoryRelease = !category.category_release;
      dispatch({
        type: MOOD_CATEGORY_CATEGORY_RELEASE,
        payload: { category, categoryRelease },
      });

      Api.updateCategory(dispatch, category.id, { category_release: categoryRelease });

      resolve();
    });
  };
}

export function toggleCategoryExcludeFromTotal(category) {
  return (dispatch, getState) => {
    return new Promise(resolve => {
      const excludeFromTotal = !category.exclude_from_total;
      dispatch({
        type: MOOD_CATEGORY_EXCLUDE_FROM_TOTAL,
        payload: { category, excludeFromTotal },
      });

      Api.updateCategory(dispatch, category.id, { exclude_from_total: excludeFromTotal }, () => {
        dispatch({ type: MOOD_RECOUNT_TOTAL });
      });

      resolve();
    });
  };
}

// Marks the category as one of the room's priority pieces — the "PRIORITY"
// badge on the category title, both here and on the customer inspiration page.
// The column is still named primary_piece; only the label was renamed.
const PRIMARY_PIECE_TOGGLE_DEBOUNCE_MS = 400;
const primaryPieceToggleDebounce = {};

const applyPrimaryPieceToggle = (dispatch, getState, category) => {
  return new Promise(resolve => {
    // Read the category back out of the store rather than trusting the object
    // the click closed over: the debounce swallows repeats, so what the caller
    // holds can predate the toggle that actually landed.
    const categories = getState().moodboard.categories || [];
    const current = categories.find(c => c.id === category.id) || category;

    // Api.updateCategory raises category.loading for the whole round trip and
    // lowers it again on success AND on failure, so this drops a toggle while
    // an update for the same category is still in flight. The requests are
    // fire-and-forget with no ordering guarantee, so two of them racing can
    // land in either order and leave the server disagreeing with the
    // optimistic state dispatched below. The flag is shared with the category's
    // other updates (rename, add product), which means a toggle during one of
    // those is dropped too — a click that has to be repeated is a better
    // outcome than a badge that silently disagrees with the database.
    if (current.loading) {
      resolve();
      return;
    }

    const primaryPiece = !current.primary_piece;
    dispatch({
      type: MOOD_CATEGORY_PRIMARY_PIECE,
      payload: { category: current, primaryPiece },
    });

    Api.updateCategory(dispatch, current.id, { primary_piece: primaryPiece });

    resolve();
  });
};

// Two guards, covering different windows. The leading-edge debounce is per
// category: the first click applies right away and repeats inside the window
// are dropped, killing a double click before it can reach the network at all.
// The in-flight check above then covers the rest of the request, which routinely
// outlasts the debounce window.
export function toggleCategoryPrimaryPiece(category) {
  if (!primaryPieceToggleDebounce[category.id]) {
    primaryPieceToggleDebounce[category.id] = debounce(
      applyPrimaryPieceToggle,
      PRIMARY_PIECE_TOGGLE_DEBOUNCE_MS,
      {
        leading: true,
        trailing: false,
      },
    );
  }
  const runToggle = primaryPieceToggleDebounce[category.id];

  return (dispatch, getState) => {
    return runToggle(dispatch, getState, category);
  };
}

// categoryToCopyDialogClose

export function categoryToCopyDialogOpen(category) {
  return (dispatch, getState) => {
    dispatch({
      type: MOOD_CATEGORY_COPY_DIALOG,
      payload: { category },
    });
  };
}

export function categoryToCopyDialogClose() {
  return categoryToCopyDialogOpen(null);
}

// export function toggleCopyCategory(category) {
//   return (dispatch, getState) => {
//     return new Promise(resolve => {
//       // const prepareForCopy = !category.prepareForCopy;
//       const prepareForCopy = true;
//       dispatch({
//         type: MOOD_CATEGORY_COPY_DIALOG,
//         payload: { category, prepareForCopy },
//       });
//       // Api.prepare_category_for_copy(dispatch, category.id);
//       resolve();
//     });
//   };
// }

export function switchCategoryRename(category, isEditMode) {
  return (dispatch, getState) => {
    return new Promise(resolve => {
      dispatch({
        type: MOOD_CATEGORY_SWITCH_RENAME,
        payload: { category, isEditMode },
      });

      if (!isEditMode) {
        Api.updateCategory(dispatch, category.id, { name: category.name });
      }

      resolve();
    });
  };
}

export function renameCategory(category, newName) {
  return { type: MOOD_CATEGORY_RENAME, payload: { category: category, newName: newName } };
}

export function updateMoodboardProduct(categoryId, product, event) {
  return (dispatch, getState) => {
    return new Promise(resolve => {
      dispatch({
        type: MOOD_UPDATE_PRODUCT,
        payload: { categoryId, product: product },
      });
      Api.updateMoodboardProduct(dispatch, categoryId, product);

      resolve();
    });
  };
}

export function removeProduct(categoryId, list, product) {
  let confirmation = confirm('Do you really want to remove the item?');
  if (confirmation) {
    return (dispatch, getState) => {
      return new Promise(resolve => {
        dispatch({
          type: MOOD_REMOVE_PRODUCT,
          payload: { categoryId, list, product: product },
        });

        Api.removeProduct(dispatch, categoryId, list, product);

        resolve();
      });
    };
  }
  return { type: null, payload: null };
}

export function selectProductOnly(product) {
  return { type: MOOD_SELECT_PRODUCT, payload: { product: product } };
}

export const setProductImageSearched = productImageSearchData => {
  // debugger;
  return {
    type: MOOD_SEARCH_PRODUCT_BY_IMAGE,
    payload: { ...productImageSearchData },
  };
};

export function selectProduct(product) {
  return (dispatch, getState) => {
    dispatch(selectProductOnly(product));
    dispatch(switchTab(PRODUCTS_TAB));
  };
}

export function imageSearch(product) {
  return (dispatch, getState) => {
    dispatch(selectProductOnly(null));
    dispatch(switchTab(PRODUCTS_TAB));
    dispatch(
      setProductImageSearched({
        url: product.preview_images_urls[0],
        thumbnailUrl: product.preview_images_urls[0],
        product,
      }),
    );
  };
}

export function createCategory(dragObj, category, event) {
  let product = dragObj.product;
  if (product) {
    return (dispatch, getState) => {
      return new Promise(resolve => {
        event.stopPropagation();

        dispatch({ type: MOOD_DROP_BEFORE_CREATE, payload: null });
        Api.createCategory(dispatch, product, category, data => {
          dispatch({ type: MOOD_CATEGORY_CREATE, payload: data });

          if (dragObj.originCategory) {
            Api.removeProduct(dispatch, dragObj.originCategory.id, dragObj.originList, product);
          }
        });

        resolve();
      });
    };
  }
  return { type: null, payload: null };
}

export function removeCategory(category) {
  let confirmation = confirm('Do you really want to remove the category?');
  if (confirmation) {
    return (dispatch, getState) => {
      return new Promise(resolve => {
        Api.removeCategory(dispatch, category.id, data =>
          dispatch({ type: MOOD_CATEGORY_REMOVE, payload: category }),
        );
      });
    };
  }
  return { type: null, payload: null };
}

export const createNewCategoryPlaceholder = category => {
  return {
    type: MOOD_CATEGORY_PLACEHOLDER,
    payload: { category, action: CREATE_PLACEHOLDER_ACTION },
  };
};

export const removeNewCategoryPlaceholder = () => {
  return { type: MOOD_CATEGORY_PLACEHOLDER, payload: { action: REMOVE_PLACEHOLDER_ACTION } };
};

// ------------------------------------
// Functions
// ------------------------------------

const processLoading = (state, action) => {
  let newState = clone(state);
  newState.categories = newState.categories.map(category => {
    if (category.id === action.payload.categoryId) {
      category.loading = action.payload.loading;
    }
    return category;
  });
  return newState;
};

const processProductRemove = (state, action) => {
  let newState = clone(state);

  if (action.payload.categoryId && action.payload.product) {
    let processCategory = cat => {
      cat.active = false;
      if (cat.id === action.payload.categoryId) {
        let list = getCategoryList(cat, action);
        let index = list.findIndex(i => i.id === action.payload.product.id);
        if (index != -1) {
          list.splice(index, 1);
        }
      }
      return cat;
    };
    newState.categories = newState.categories.map(processCategory);
    recountTotal(newState);
    recountProductsOverlays(newState);
  }

  return newState;
};

const updateProductInArray = (products, newProduct) => {
  return products.map(product => {
    return product.id === newProduct.id ? newProduct : product;
  });
};

const processProductUpdate = (state, action) => {
  const newState = clone(state);
  const { categories } = newState;

  if (categories) {
    const categoryIndex = categories.findIndex(({ id }) => id === action.payload.categoryId);
    const category = categories[categoryIndex];
    const { products, alternatives } = category;
    category.products = updateProductInArray(products, action.payload.product);
    category.alternatives = updateProductInArray(alternatives, action.payload.product);
    categories[categoryIndex] = category;
    recountTotal(newState);
    return newState;
  }
  return state;
};

const processProductSelect = (state, action) => {
  let newState = clone(state);
  newState.selectedProduct = action.payload.product;
  newState.productImageSearched = null;
  return newState;
};

const processSearchProductByImage = (state, action) => {
  let newState = clone(state);
  newState.productImageSearched = action.payload;
  newState.selectedProduct = null;
  return newState;
};

const processSwitchRename = (state, action) => {
  let newState = clone(state);
  let processCategory = cat => {
    let newCat = { ...cat };
    if (cat.id === action.payload.category.id) {
      newCat.isEditMode = action.payload.isEditMode;
    }
    return newCat;
  };
  newState.categories = newState.categories.map(processCategory);
  return newState;
};

const processCategoryAttrsUpdate = (state, categoryId, callbackFn) => {
  const newState = clone(state);
  const processCategory = cat => {
    let newCat = cat;
    if (cat.id === categoryId) {
      newCat = clone(cat);
      callbackFn(newCat);
    }
    return newCat;
  };
  newState.categories = newState.categories.map(processCategory);
  return newState;
};

const processRename = (state, action) => {
  const newState = processCategoryAttrsUpdate(state, action.payload.category.id, newCat => {
    newCat.name = action.payload.newName;
  });
  recountProductsOverlays(newState);
  return newState;
};

const processCreate = (state, action) => {
  let newState = clone(state, { drag: {} });

  let index = newState.categories.findIndex(i => i.id === -1);
  if (index !== -1) {
    newState.categories.splice(index, 1, action.payload);
  }

  recountTotal(newState);
  recountProductsOverlays(newState);
  showOnboardingMessage(newState);
  return newState;
};

const processRemove = (state, action) => {
  let newState = clone(state);
  const index = newState.categories.findIndex(i => i.id === action.payload.id);
  if (index !== -1) {
    newState.categories.splice(index, 1);
  }
  recountTotal(newState);
  recountProductsOverlays(newState);
  return newState;
};

const processCategoryToggleFavourite = (state, action) => {
  const newState = processCategoryAttrsUpdate(state, action.payload.category.id, newCat => {
    newCat.favourite = action.payload.favourite;
  });
  return newState;
};

const processCategoryOpenCopyDialog = (state, action) => {
  const newState = clone(state);
  const { category } = action.payload;
  const { copyCategoryDialog } = state;
  const params = { copyCategoryDialog: { ...copyCategoryDialog, category } };
  return { ...newState, ...params };
};

const processCategoryRelease = (state, action) => {
  const newState = processCategoryAttrsUpdate(state, action.payload.category.id, newCat => {
    newCat.category_release = action.payload.categoryRelease;
  });
  return newState;
};

const processCategoryExcludeFromTotal = (state, action) => {
  return processCategoryAttrsUpdate(state, action.payload.category.id, newCat => {
    newCat.exclude_from_total = action.payload.excludeFromTotal;
  });
};

const processCategoryPrimaryPiece = (state, action) => {
  return processCategoryAttrsUpdate(state, action.payload.category.id, newCat => {
    newCat.primary_piece = action.payload.primaryPiece;
  });
};

const processRecountTotal = (state, action) => {
  const newState = clone(state);
  recountTotal(newState);
  return newState;
};

const processCategoryPlaceholder = (state, action) => {
  if (!state.drag.product) {
    return state;
  }

  const existing = state.categories.findIndex(i => i.id === -1);
  const creating = action.payload.action === CREATE_PLACEHOLDER_ACTION;
  // Every mousemove during a product drag dispatches a REMOVE; when there is no
  // placeholder to remove there is nothing to do, so hand back the same state.
  if (existing === -1 && !creating) {
    return state;
  }

  const categories = [...state.categories];
  if (existing !== -1) {
    categories.splice(existing, 1);
  }

  if (creating) {
    let placeholder = { id: -1, column: action.payload.category.column };

    let index = categories.findIndex(i => i.id === action.payload.category.id);
    if (index !== -1) {
      placeholder.position = getCategoryPos(categories, action.payload.category);
      categories.splice(index, 0, placeholder);
    } else {
      placeholder.position = getColumnSize(categories, action.payload.category.column);
      categories.push(placeholder);
    }
  }

  return { ...state, categories };
};

const processDropBeforeCreate = (state, action) => {
  // Nothing below the top level changes, so a deep clone was pure waste.
  return { ...state, drag: {} };
};

// ------------------------------------
// Action Handlers
// ------------------------------------
export const CATEGORIES_ACTION_HANDLERS = {
  [MOOD_CATEGORY_LOADING]: processLoading,
  [MOOD_REMOVE_PRODUCT]: processProductRemove,
  [MOOD_SELECT_PRODUCT]: processProductSelect,
  [MOOD_SEARCH_PRODUCT_BY_IMAGE]: processSearchProductByImage,
  [MOOD_CATEGORY_SWITCH_RENAME]: processSwitchRename,
  [MOOD_CATEGORY_CATEGORY_RELEASE]: processCategoryRelease,
  [MOOD_CATEGORY_EXCLUDE_FROM_TOTAL]: processCategoryExcludeFromTotal,
  [MOOD_CATEGORY_PRIMARY_PIECE]: processCategoryPrimaryPiece,
  [MOOD_RECOUNT_TOTAL]: processRecountTotal,
  [MOOD_CATEGORY_TOGGLE_FAVOURITE]: processCategoryToggleFavourite,
  [MOOD_CATEGORY_COPY_DIALOG]: processCategoryOpenCopyDialog,
  [MOOD_CATEGORY_RENAME]: processRename,
  [MOOD_CATEGORY_CREATE]: processCreate,
  [MOOD_CATEGORY_REMOVE]: processRemove,
  [MOOD_UPDATE_PRODUCT]: processProductUpdate,
  [MOOD_CATEGORY_PLACEHOLDER]: processCategoryPlaceholder,
  [MOOD_DROP_BEFORE_CREATE]: processDropBeforeCreate,
};



// WEBPACK FOOTER //
// ./app/javascript/design/routes/Moodboard/modules/moodboard-categories.js